import { t as localeText } from "@/i18n/translate";
import { useTranslation as useLocaleSubscription } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { uploadAsset } from "@/api/assets";
import { queryKeys } from "@/api/query-keys";
import type { MediaToolReference } from "@/features/media-tools";
import { FileDropzone, IconButton, Spinner } from "@/shared/ui";
import { CloseIcon, ImageIcon } from "@/shared/ui/icons";
import { referenceProblem, settingLabel, type ThreeDAsset } from "./threeDState";
import { ThreeDAssetPicker } from "./ThreeDAssetPicker";

export function ThreeDReferenceInput({ input, asset, selectedIDs, projectID, error, onChange, compact = false }: {
  compact?: boolean;
  input: MediaToolReference; asset?: ThreeDAsset; selectedIDs: string[]; projectID?: string; error?: string;
  onChange: (asset?: ThreeDAsset) => void;
}) {
  useLocaleSubscription();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [failure, setFailure] = useState("");
  const mounted = useRef(true);
  const busy = useRef(false);
  const label = settingLabel({ key: input.role ?? input.key });
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (!uploading) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploading]);
  async function upload(files: File[]) {
    if (busy.current || !files[0]) return;
    busy.current = true; setUploading(true); setFailure("");
    try {
      const next = await uploadAsset(files[0], { visibility: "private", rightsAttested: false });
      void client.invalidateQueries({ queryKey: queryKeys.foundation.all });
      if (!mounted.current) return;
      const problem = referenceProblem(next, input);
      if (problem) { setFailure(problem); return; }
      onChange(next); setOpen(false);
    } catch {
      if (mounted.current) setFailure("This image couldn’t be uploaded. Try again.");
    } finally {
      busy.current = false;
      if (mounted.current) setUploading(false);
    }
  }
  return <div className={compact ? "studio-prompt-tile" : "three-d-source-slot"} data-field-key={input.role ?? input.key}>
    {compact ? <>
      <button type="button" className="studio-prompt-tile-add" aria-label={`${asset ? localeText("interface.change_64fbd995", { defaultValue: "Change" }) : localeText("interface.choose_78b7c9f6", { defaultValue: "Choose" })} ${label.toLowerCase()}`} title={label}
        disabled={uploading} onClick={() => setOpen(true)}>
        {uploading ? <Spinner label={localeText("interface.uploading_image_1a4e93d0", { defaultValue: "Uploading image…" })} /> : asset ? <img src={asset.thumbnail_url ?? asset.url} alt={asset.name} /> : <><ImageIcon /><span className="studio-prompt-view-label">{label}</span></>}
      </button>
      {asset ? <button type="button" className="studio-prompt-tile-remove" aria-label={localeText("interface.remove_value0_4945934f", { defaultValue: "Remove {{value0}}", value0: label.toLowerCase() })} disabled={uploading} onClick={() => onChange(undefined)}><CloseIcon /></button> : null}
      {failure || error ? <p role="alert" className="studio-prompt-alert">{failure || error}</p> : null}
    </> : <>    <FileDropzone label={label} aria-label={`${asset ? localeText("interface.change_64fbd995", { defaultValue: "Change" }) : localeText("interface.choose_78b7c9f6", { defaultValue: "Choose" })} ${label.toLowerCase()}`}
      accept={input.accepted_formats?.join(",") ?? "image/png,image/jpeg,image/webp"} maxFileSize={input.max_bytes}
      className="three-d-view-dropzone" disabled={uploading} onBrowse={() => setOpen(true)} onFilesSelected={files => void upload(files)}
      error={failure || error}
      preview={uploading ? <Spinner label={localeText("interface.uploading_image_1a4e93d0", { defaultValue: "Uploading image…" })} /> : asset ? <img src={asset.thumbnail_url ?? asset.url} alt={asset.name} width={240} height={160} /> : undefined} />
    {asset ? <><IconButton size="sm" className="three-d-source-remove" aria-label={localeText("interface.remove_value0_4945934f", { defaultValue: "Remove {{value0}}", value0: label.toLowerCase() })} disabled={uploading} onClick={() => onChange(undefined)}><CloseIcon /></IconButton><span className="three-d-source-caption">{label}</span></> : null}
</>}
    {open ? <ThreeDAssetPicker input={input} projectID={projectID} selectedIDs={selectedIDs} uploading={uploading}
      onUpload={files => void upload(files)} error={failure} onClose={() => setOpen(false)}
      onSelect={next => { setFailure(""); onChange(next); setOpen(false); }} /> : null}
  </div>;
}
